import { FC, useEffect, useState } from "react";
import {
  Button,
  InputGroup,
  InputSelect,
  NO_REGEX,
  PhoneInputGroup,
  color,
} from "@funded-here-interface/common";
import { NumberInput, Group, Text } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../styles/BizProfileCss";
import { useStyles as useShareholderStyles } from "../../components/ShareholdersDirectors/ShareholdersDirectors.styles";
import { useSelector } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import {
  EmploymentPosition,
  EmploymentStatus,
  Industry,
  IndustryGroup,
  ReferralSource,
} from "../../constants/enums";
import { IndustryGroupMap } from "../../constants/dropdownValues";
import {
  CountryCode,
  countries,
  countryNameToCodeMap,
} from "@funded-here-interface/common/src/constant/country";
import { useDisclosure } from "@mantine/hooks";
import { toast } from "react-toastify";
import IndInvestorDeclarationModal from "./Components/IndInvestorDeclarationModal/IndInvestorDeclarationModal";
import { camelCaseToNormalCase } from "../../Utils/formatter";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import { formatDateDraft } from "@funded-here-interface/common/src/Utils/formatter";
import { isValidPhoneNumber } from "react-phone-number-input";
import {
  getIndonesiaStateOptions,
  getStateIdByName,
} from "@funded-here-interface/common/src/constant/indonesia";
import { useGetIndonesiaCitiesQuery } from "@funded-here-interface/common/src/hooks/useGetIndonesiaCities";
import { IndonesiaCity } from "@funded-here-interface/common/src/services/getIndonesiaCities";

const initialFormState = {
  industryGroup: "",
  industryDropdownValues: [] as Industry[],
  industry: "",
  industryOthers: "",
  occupation: "",
  experience: "",
  phoneNumber: "",
  nationality: "",
  gender: "",
  employmentStatus: "",
  employmentPosition: "",
  dateOfBirth: "",
  countryOfBirth: "",
  countryOfResidence: "",
  region: "",
  city: "",
  address: "",
  postalCode: "",
  investmentAmount: 0,
  isPep: null as boolean | null,
  employerName: "",
  referralSource: "",
  otherReferral: "",
  numberOfTaxResidenceCountries: 1,
  taxResidenceCountries: [
    { country: "", tin: "", reason: "", explanation: "" },
  ] as Array<{
    country: string;
    tin: string;
    reason: string;
    explanation: string;
  }>,
};

const initialErrorState = Object.keys(initialFormState).reduce((acc, key) => {
  acc[key] = "";
  return acc;
}, {} as Record<string, string>);

const BizProfileInd: FC = () => {
  const { classes } = useStyles();
  const { classes: shareholderClasses } = useShareholderStyles();
  const Navigate = useNavigate();

  const { investorDeclarationQualified } = useSelector(
    (state: RootState) => state.prof
  );

  const [opened, { close }] = useDisclosure(!investorDeclarationQualified);

  const userId = useSelector((state: RootState) => state.auth.userId);
  const { token } = useSelector((state: RootState) => state.auth);

  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  const [form, setForm] = useState({ ...initialFormState });
  const [error, setError] = useState({ ...initialErrorState });
  const [countryCode, setCountryCode] = useState<CountryCode | undefined>();
  const [initPage, setInitPage] = useState(true);

  // State for Indonesia region selection
  const [selectedStateId, setSelectedStateId] = useState<string | null>(null);

  // countryOfResidence — individual investor personal residence
  const isIndonesia = form.countryOfResidence === "Indonesia";

  // Indonesia cities query with caching - only fetches when stateId changes
  const { data: citiesData, isLoading: isCitiesLoading } =
    useGetIndonesiaCitiesQuery({
      stateId: selectedStateId,
      token,
    });

  // Derive city options from cached query data
  const cityOptions = citiesData?.map((c: IndonesiaCity) => c.value) || [];

  // Helper: Populate form from draft
  const populateFormFromDraft = (data: any) => {
    setForm({
      industryGroup: data.industryGroup || "",
      industryDropdownValues:
        IndustryGroupMap[data.industryGroup as IndustryGroup] || [],
      industry: data.industry || "",
      industryOthers: data.industryOthers || "",
      occupation: data.occupation || "",
      experience: data.numberofYears?.toString() || "",
      phoneNumber: data.phoneNumber || "",
      nationality: data.nationality || "",
      gender: data.gender || "",
      employmentStatus: data.employmentStatus || "",
      employmentPosition: data.employmentPosition || "",
      dateOfBirth: data.dateOfBirth ? formatDateDraft(data.dateOfBirth) : "",
      countryOfBirth: data.countryOfBirth || "",
      countryOfResidence: data.countryOfResidence || "",
      region: data.region || "",
      city: data.city || "",
      address: data.legalEntityAddress || "",
      postalCode: data.postalCode?.toString() || "",
      investmentAmount: Number(data.investmentAmount) || 0,
      isPep: typeof data.isPep === "boolean" ? data.isPep : null,
      employerName: data.employerName || "",
      referralSource: data.referralSource || "",
      otherReferral: data.otherReferral || "",
      numberOfTaxResidenceCountries: data.numberOfTaxResidenceCountries || 1,
      taxResidenceCountries: data.taxResidenceCountries?.length
        ? data.taxResidenceCountries
        : [{ country: "", tin: "", reason: "", explanation: "" }],
    });
    if (data.countryOfResidence) {
      const code = countryNameToCodeMap.get(data.countryOfResidence);
      setCountryCode(code as CountryCode);
    }

    // If Indonesia and region is set, set stateId to trigger cached city fetch
    if (data.countryOfResidence === "Indonesia" && data.region) {
      const stateId = getStateIdByName(data.region);
      setSelectedStateId(stateId || null);
    }
  };

  // Fetch draft on mount
  useEffect(() => {
    const fetchDraft = async () => {
      if (initPage) {
        try {
          const params = { userId: parseInt(userId), token };
          const data = await getOrgDraftMutation.mutateAsync(params);
          if (data) {
            populateFormFromDraft(data);
          }
        } catch (error) {
          toast.error(`Error fetching draft: ${error}`);
        } finally {
          setInitPage(false);
        }
      }
    };
    fetchDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initPage, userId, token]);

  useEffect(() => {
    if (investorDeclarationQualified === false) {
      Navigate("/unqualified-investor");
    }
  }, [investorDeclarationQualified, Navigate]);

  // Generic change handler
  const handleFieldChange = (field: keyof typeof form, value: any) => {
    // Prevent future dates for date fields
    if (field === "dateOfBirth" && value && new Date(value) > new Date()) {
      setError((prev) => ({
        ...prev,
        [field]: "Date of Birth cannot be in the future",
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [field]: value }));
    setError((prev) => ({ ...prev, [field]: "" }));
  };

  // Special handlers
  const handleIndustryGroupChange = (value: IndustryGroup) => {
    handleFieldChange("industryGroup", value);
    handleFieldChange("industry", "");
    handleFieldChange("industryDropdownValues", IndustryGroupMap[value] || []);
  };

  const handleCountryOfResidenceChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      countryOfResidence: value,
      region: "",
      city: "",
    }));
    setError((prev) => ({ ...prev, countryOfResidence: "", region: "", city: "" }));
    setSelectedStateId(null);
    const code = countryNameToCodeMap.get(value);
    setCountryCode(code as CountryCode);
  };

  const handleRegionChange = (value: string) => {
    setForm((prev) => ({ ...prev, region: value, city: "" }));
    setError((prev) => ({ ...prev, region: "", city: "" }));
    const stateId = getStateIdByName(value);
    setSelectedStateId(stateId || null);
  };

  const handleCityChange = (value: string) => {
    setForm((prev) => ({ ...prev, city: value }));
    setError((prev) => ({ ...prev, city: "" }));
  };

  // Tax residency handlers
  const handleNumberOfTaxResidenceCountriesChange = (value: number | "") => {
    const count = typeof value === "number" ? value : 0;
    const maxCount = Math.min(count, 5);
    handleFieldChange("numberOfTaxResidenceCountries", maxCount);

    // Initialize or trim tax residence countries array
    const currentCountries = form.taxResidenceCountries;
    const newCountries = [];

    for (let i = 0; i < maxCount; i++) {
      if (i < currentCountries.length) {
        newCountries.push(currentCountries[i]);
      } else {
        newCountries.push({
          country: "",
          tin: "",
          reason: "",
          explanation: "",
        });
      }
    }

    handleFieldChange("taxResidenceCountries", newCountries);
  };

  const handleTaxResidenceCountryChange = (
    index: number,
    field: keyof (typeof form.taxResidenceCountries)[0],
    value: string
  ) => {
    const updatedCountries = [...form.taxResidenceCountries];
    updatedCountries[index] = { ...updatedCountries[index], [field]: value };

    // Clear reason and explanation if TIN is not "-"
    if (field === "tin" && value !== "-") {
      updatedCountries[index].reason = "";
      updatedCountries[index].explanation = "";
    }

    // Clear explanation if reason is not "Reason B"
    if (field === "reason" && value !== "Reason B") {
      updatedCountries[index].explanation = "";
    }

    handleFieldChange("taxResidenceCountries", updatedCountries);
  };

  // Validation
  const validateForm = () => {
    let valid = true;
    const customError: typeof error = { ...initialErrorState };

    // The fields below are optional or require special handling
    const specialErrorHandling = [
      "industryOthers",
      "investmentAmount",
      "isPep",
      "otherReferral",
      "numberOfTaxResidenceCountries",
      "taxResidenceCountries",
    ];

    // Region and city are only required for Indonesia
    if (!isIndonesia) {
      specialErrorHandling.push("region", "city");
    }

    Object.entries(form).forEach(([key, value]) => {
      // Skip validation for fields that are optional or have special handling
      if (!specialErrorHandling.includes(key) && !value) {
        valid = false;
        customError[key] = `${camelCaseToNormalCase(key)} is required`;
      }
    });

    if (!form.investmentAmount) {
      valid = false;
      customError.investmentAmount = "Planned Investment Amount is required";
    }

    if (form.isPep === null) {
      valid = false;
      customError.isPep = "Please select if you are a PEP";
    }

    if (form.referralSource === ReferralSource.OTHER && !form.otherReferral) {
      valid = false;
      customError.otherReferral = "Please specify how you found out about us";
    }

    if (!isValidPhoneNumber(String(form.phoneNumber))) {
      valid = false;
      customError.phoneNumber = "Phone Number is not valid";
    }

    // Tax residency validation - must specify at least 1 country
    if (form.numberOfTaxResidenceCountries === 0) {
      valid = false;
      customError.numberOfTaxResidenceCountries =
        "Please specify at least 1 country of tax residence";
    }

    if (form.numberOfTaxResidenceCountries > 0) {
      form.taxResidenceCountries.forEach((country, index) => {
        if (!country.country) {
          valid = false;
          customError[`taxResidenceCountry${index}`] =
            "Country of Tax Residence is required";
        }

        if (!country.tin) {
          valid = false;
          customError[`taxResidenceTin${index}`] = "TIN is required";
        }

        if (country.tin === "-" && !country.reason) {
          valid = false;
          customError[`taxResidenceReason${index}`] =
            "Reason is required when TIN is unavailable";
        }

        if (country.reason === "Reason B" && !country.explanation) {
          valid = false;
          customError[`taxResidenceExplanation${index}`] =
            "Explanation is required for Reason B";
        }
      });
    }

    // Validate date of birth is not in the future
    if (form.dateOfBirth && new Date(form.dateOfBirth) > new Date()) {
      valid = false;
      customError.dateOfBirth = "Date of Birth cannot be in the future";
    }

    setError(customError);
    return valid;
  };

  // Save draft
  const saveDraft = (showToast: boolean = true) => {
    // If country is not Indonesia, explicitly clear region and city
    const clearFields: ("region" | "city")[] = !isIndonesia
      ? ["region", "city"]
      : [];

    saveOrgDraftMutation.mutateAsync(
      {
        userId: parseInt(userId),
        token,
        industryGroup: form.industryGroup,
        industry: form.industry,
        occupation: form.occupation,
        numberofYears: parseInt(form.experience),
        nationality: form.nationality,
        gender: form.gender,
        employmentStatus: form.employmentStatus,
        employmentPosition: form.employmentPosition,
        dateOfBirth: form.dateOfBirth ? form.dateOfBirth : null,
        countryOfBirth: form.countryOfBirth,
        countryOfResidence: form.countryOfResidence,
        region: form.region || null,
        city: form.city || null,
        legalEntityAddress: form.address,
        postalCode: form.postalCode ? form.postalCode : null,
        phoneNumber: form.phoneNumber || null,
        investmentAmount: form.investmentAmount,
        isPep: form.isPep,
        employerName: form.employerName,
        referralSource: form.referralSource,
        otherReferral: form.otherReferral,
        numberOfTaxResidenceCountries: form.numberOfTaxResidenceCountries,
        taxResidenceCountries: form.taxResidenceCountries,
        clearFields,
      },
      {
        onSuccess: () => {
          if (showToast) toast.success("Draft Saved");
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  // Submit handler
  const handleSubmit = async () => {
    if (validateForm()) {
      await saveDraft();
      Navigate("/bank-details-ind");
    }
  };

  return (
    <>
      <IndInvestorDeclarationModal
        onError={toast.error}
        opened={opened}
        close={close}
      />
      <div style={{ marginTop: "70px" }}>
        <InputSelect
          isErrored={error.industryGroup}
          errorMessage
          onChange={handleIndustryGroupChange}
          labelText="Industry Group"
          value={form.industryGroup}
          id="industryGroup"
          data={Object.values(IndustryGroup).sort((a, b) => a.localeCompare(b))}
        />
        <InputSelect
          isErrored={error.industry}
          errorMessage
          onChange={(value: Industry) => handleFieldChange("industry", value)}
          labelText="Industry"
          value={form.industry}
          id="institution_type"
          data={[...form.industryDropdownValues].sort((a, b) =>
            a.localeCompare(b)
          )}
        />
        <InputSelect
          isErrored={error.employmentStatus}
          errorMessage
          onChange={(value: string) =>
            handleFieldChange("employmentStatus", value)
          }
          labelText="Employment Status"
          id="employmentStatus"
          value={form.employmentStatus}
          data={Object.values(EmploymentStatus)}
        />
        <InputSelect
          isErrored={error.employmentPosition}
          errorMessage
          onChange={(value: string) =>
            handleFieldChange("employmentPosition", value)
          }
          labelText="Employment Position"
          id="employmentPosition"
          value={form.employmentPosition}
          data={Object.values(EmploymentPosition)}
        />
        <InputGroup
          isErrored={error.occupation}
          errorMessage
          onChange={(value: string) => handleFieldChange("occupation", value)}
          labelText="Specify Occupation"
          id="name"
          value={form.occupation}
        />
        <InputGroup
          isErrored={error.experience}
          errorMessage
          onChange={(value: string) =>
            handleFieldChange("experience", value.replace(NO_REGEX, ""))
          }
          labelText="No of years in this occupation"
          id="experience"
          value={form.experience}
        />
        <InputSelect
          isErrored={error.gender}
          errorMessage
          onChange={(value: string) => handleFieldChange("gender", value)}
          labelText="Gender"
          id="gender"
          value={form.gender}
          data={["Female", "Male"]}
        />
        <InputGroup
          isErrored={error.dateOfBirth}
          errorMessage
          onChange={(value: any) => handleFieldChange("dateOfBirth", value)}
          labelText="Date of Birth"
          value={form.dateOfBirth}
          id="dateOfBirth"
          type="date"
          max={new Date().toISOString().split("T")[0]}
        />
        <InputSelect
          isErrored={error.countryOfBirth}
          errorMessage
          onChange={(value: string) =>
            handleFieldChange("countryOfBirth", value)
          }
          labelText="Country of Birth"
          id="countryOfBirth"
          value={form.countryOfBirth}
          data={countries}
        />
        <InputSelect
          isErrored={error.nationality}
          errorMessage
          onChange={(value: string) => handleFieldChange("nationality", value)}
          labelText="Nationality"
          id="nationality"
          value={form.nationality}
          data={countries}
        />
        <InputSelect
          isErrored={error.countryOfResidence}
          errorMessage
          onChange={handleCountryOfResidenceChange}
          labelText="Country of residence"
          id="countryOfResidence"
          value={form.countryOfResidence}
          data={countries}
        />
        {isIndonesia && (
          <>
            <InputSelect
              isErrored={error.region}
              errorMessage
              onChange={handleRegionChange}
              labelText="Region"
              id="region"
              value={form.region}
              data={getIndonesiaStateOptions()}
            />
            <InputSelect
              isErrored={error.city}
              errorMessage
              onChange={handleCityChange}
              labelText={isCitiesLoading ? "City (Loading...)" : "City"}
              id="city"
              value={form.city}
              data={cityOptions}
              disabled={!form.region || isCitiesLoading}
            />
          </>
        )}
        <InputGroup
          isErrored={error.address}
          errorMessage
          onChange={(value: string) => handleFieldChange("address", value)}
          labelText="Address"
          id="address"
          value={form.address}
        />
        <InputGroup
          isErrored={error.postalCode}
          errorMessage
          onChange={(value: string) =>
            handleFieldChange("postalCode", value.replace(NO_REGEX, ""))
          }
          labelText="Postal Code"
          id="postalcode"
          value={form.postalCode}
        />
        <InputGroup
          isErrored={error.investmentAmount}
          errorMessage
          onChange={(value: string) =>
            handleFieldChange(
              "investmentAmount",
              parseInt(value.replace(NO_REGEX, "")) || 0
            )
          }
          labelText="Planned Investment Amount"
          id="investmentAmount"
          value={`${form.investmentAmount || 0}`}
        />
        <div className={classes.phoneNumberWrapper}>
          <PhoneInputGroup
            isErrored={error.phoneNumber}
            value={form.phoneNumber}
            country={countryCode}
            onChange={(value: string) =>
              handleFieldChange("phoneNumber", value)
            }
          />
        </div>
        <div>
          <p
            style={{
              paddingBottom: "0px",
              paddingLeft: "0px",
              color: "inherit",
              textAlign: "left",
              fontSize: "0.875rem",
            }}
          >
            Are you a politically exposed person (PEP)?
          </p>
          <div style={{ display: "flex", margin: "10px 0" }}>
            <div style={{ marginRight: "10px" }}>
              <input
                type="radio"
                name="isPep"
                value="yes"
                id="isPepYes"
                checked={form.isPep === true}
                onChange={() => handleFieldChange("isPep", true)}
              />
              <label htmlFor="isPepYes" style={{ paddingLeft: "3px" }}>
                Yes
              </label>
            </div>
            <div>
              <input
                type="radio"
                name="isPep"
                value="no"
                id="isPepNo"
                checked={form.isPep === false}
                onChange={() => handleFieldChange("isPep", false)}
              />
              <label htmlFor="isPepNo" style={{ paddingLeft: "3px" }}>
                No
              </label>
            </div>
          </div>
          {error.isPep && (
            <p style={{ fontSize: "14px", color: "#fa5252" }}>{error.isPep}</p>
          )}
        </div>
        <InputGroup
          isErrored={error?.employerName}
          errorMessage
          onChange={(value: string) => handleFieldChange("employerName", value)}
          labelText="Current Employer Name"
          id="employerName"
          value={form.employerName}
        />
        <InputSelect
          isErrored={error.referralSource}
          errorMessage
          onChange={(value: string) => {
            handleFieldChange("referralSource", value);
            if (value !== ReferralSource.OTHER) {
              handleFieldChange("otherReferral", null);
            }
          }}
          labelText="How Did You Hear About Us?"
          id="referralSource"
          value={form.referralSource}
          data={Object.values(ReferralSource)}
        />
        {form.referralSource === ReferralSource.OTHER && (
          <InputGroup
            isErrored={error.otherReferral}
            errorMessage
            onChange={(value: string) =>
              handleFieldChange("otherReferral", value)
            }
            labelText="Briefly specify how you found out about us"
            id="otherReferral"
            value={form.otherReferral}
          />
        )}

        {/* Declaration of Tax Residency Section */}
        <div style={{ marginTop: "40px" }}>
          <p className={shareholderClasses.shareTitle}>
            Declaration of Tax Residency
          </p>
          <p className={shareholderClasses.sharePara}>
            Please complete the following fields indicating where you are tax
            resident and your TIN (Taxpayer Identification Number) for each
            jurisdiction indicated.
          </p>
          <div className={shareholderClasses.sharePara}>
            <p>
              If a <strong>TIN is unavailable</strong>, please provide the
              appropriate reason A, B or C where indicated below:{" "}
            </p>
            <ul>
              <li>
                <strong>Reason A:</strong> The jurisdiction where the Account
                Holder is liable to pay tax does not issue TINs to its
                residents.
              </li>
              <li>
                <strong>Reason B:</strong> The Account Holder is otherwise
                unable to obtain a TIN or equivalent number. (Please indicate an
                explanation below why you are unable to obtain a TIN if you have
                selected this reason)
              </li>
              <li>
                <strong>Reason C:</strong> No TIN is required. (Only select this
                reason if the authorities of the jurisdiction of tax residence
                indicated do not require the TIN to be disclosed)
              </li>
            </ul>
          </div>

          <Group>
            <Text
              className={shareholderClasses.text}
              component="label"
              size="sm"
            >
              Number of Country of Tax Residence
            </Text>
            <NumberInput
              error={error.numberOfTaxResidenceCountries}
              onChange={handleNumberOfTaxResidenceCountriesChange}
              id="numberOfTaxResidenceCountries"
              value={form.numberOfTaxResidenceCountries}
              min={1}
              size="lg"
              max={5}
              style={{ paddingBottom: "20px" }}
              onKeyDown={(e) => e.preventDefault()}
              styles={{ input: { caretColor: "transparent" } }}
            />
          </Group>

          {/* Dynamic Country Inputs */}
          {form.taxResidenceCountries.map((country, index) => (
            <div key={index} style={{ marginTop: "30px" }}>
              <InputSelect
                isErrored={error[`taxResidenceCountry${index}`]}
                errorMessage
                onChange={(value: string) =>
                  handleTaxResidenceCountryChange(index, "country", value)
                }
                labelText={`${index + 1}. Country of Tax Residence`}
                id={`taxResidenceCountry${index}`}
                value={country.country}
                data={countries}
              />

              <InputGroup
                isErrored={error[`taxResidenceTin${index}`]}
                errorMessage
                onChange={(value: string) =>
                  handleTaxResidenceCountryChange(index, "tin", value)
                }
                labelText={`${
                  index + 1
                }. Taxpayer Identification Number (TIN) (Enter '-' if unavailable)`}
                id={`taxResidenceTin${index}`}
                value={country.tin}
              />

              {country.tin === "-" && (
                <InputSelect
                  isErrored={error[`taxResidenceReason${index}`]}
                  errorMessage
                  onChange={(value: string) =>
                    handleTaxResidenceCountryChange(index, "reason", value)
                  }
                  labelText={`${index + 1}. Reason (if TIN is unavailable)`}
                  id={`taxResidenceReason${index}`}
                  value={country.reason}
                  data={["Reason A", "Reason B", "Reason C"]}
                />
              )}

              {country.reason === "Reason B" && (
                <InputGroup
                  isErrored={error[`taxResidenceExplanation${index}`]}
                  errorMessage
                  onChange={(value: string) =>
                    handleTaxResidenceCountryChange(index, "explanation", value)
                  }
                  labelText={`${
                    index + 1
                  }. Explanation (required for Reason B)`}
                  id={`taxResidenceExplanation${index}`}
                  value={country.explanation}
                />
              )}
            </div>
          ))}
        </div>
      </div>
      <div className={classes.buttonWrapper}>
        <Button
          onClick={handleSubmit}
          textColor={color.WHITE}
          backgroundColor={color.FHGREEN}
          width="100%"
        >
          Next
        </Button>
      </div>
      <div className={classes.buttonWrapper}>
        <Button
          onClick={() => saveDraft()}
          textColor={color.WHITE}
          backgroundColor={color.LOANTEXT}
          width="100%"
        >
          Save Draft
        </Button>
      </div>
    </>
  );
};

export default BizProfileInd;
